<template>
  <div class="fantasy-item-card" :class="[`card-${variant}`, { 'card-spoiled': item.state === 'spoiled' }]" @click="onCardClick">
    <slot name="before"></slot>
    <div class="img-wrapper">
      <!-- decoding="async"：图片解码不阻塞主线程，背包满格时首屏渲染不卡 -->
      <img :class="{ 'img-spoiled': item.state === 'spoiled' }" :title="itemTitle" :src="ossPath + item.imgSrc" decoding="async">
      <div v-if="item.stackCount > 1" class="stackCount">{{ item.stackCount }}</div>
      <!-- 保鲜（perish_v1）：新鲜不显 / 临期橙显剩余 / 变质灰显 -->
      <div v-if="freshMeta" class="freshness-badge" :class="freshMeta.cls">{{ freshMeta.text }}</div>
    </div>
    <div v-if="showName" class="itemName">{{ item.itemName }}</div>
    <div v-if="showInfo && infoText" class="itemInfo">{{ infoText }}</div>
    <slot></slot>
  </div>
</template>

<script>
import { effectCardText, effectLinesOf, freshRemainText } from '../../utils/itemEffect'
export default {
  name: 'FantasyItemCard',
  props: {
    item: { type: Object, required: true },
    showName: { type: Boolean, default: true },
    showInfo: { type: Boolean, default: true },
    variant: { type: String, default: 'default' },
    // 点击图片时是否停止冒泡并显示说明。默认开启（桌面 title 悬停 / 移动端点击见说明）。
    // 地面拾取等场景需要让点击冒泡到父级 @click（如 streetGround 的 pickitem），应传 false。
    showDescOnClick: { type: Boolean, default: true }
  },
computed: {
    // 保鲜（perish_v1）：三档角标——新鲜不显/临期橙显"剩X天/小时"/变质灰显"已变质"
    freshMeta() {
      const item = this.item;
      if (item.state === 'spoiled') {
        return { cls: 'spoiled', text: '已变质' };
      }
      if (item.state === 'expiring' && item.expireAt) {
        const remain = item.expireAt - Date.now();
        if (remain <= 0) {
          return { cls: 'spoiled', text: '已变质' };
        }
        return { cls: remain >= 86400000 ? 'expiring' : 'expiring hot', text: freshRemainText(item.expireAt) };
      }
      return null;
    },
    // 剩余时长文案（与角标同一函数）：已过期或无到期时间返回空串
    freshRemain() {
      return freshRemainText(this.item.expireAt);
    },
    // 效果行（体力 + 限时增益）：数值全部来自配置，描述里不写数字。
    // 口径见 utils/itemEffect.js；无 store（独立预览等）时体力那行降级为纯数值。
    effectLines() {
      return effectLinesOf(this.item, this.currentDetail());
    },
    itemTitle() {
      const item = this.item;
      let t = item.itemName || '';
      if (this.effectLines.length > 0) {
        t += '\n' + this.effectLines.join('\n');
      } else if (item.strength != null && item.strength > -1) {
        t += ' 体力:' + item.strength;
      } else if (item.durability != null && item.durability > -1) {
        t += ' 评分:' + item.score + ' 耐久:' + item.durability;
      } else if (item.score != null && item.score > -1) {
        t += ' 评分:' + item.score;
      }
      // 保鲜：绝对到期时间（不滚动、不撒谎）；变质时补一行处置提示
      if (item.state === 'spoiled') {
        t += '\n已变质：不可食用，可到果园的堆肥点堆成肥料（5 个变质物 → 1 个【肥料】）';
      } else if (item.expireAt && item.expireAt > 0) {
        const d = new Date(item.expireAt);
        const pad = n => (n < 10 ? '0' + n : n);
        // 寻物雷达委托复用同一展示位，但语义是"交付期限"而非"保质期"（宝物不会腐坏）
        t += '\n' + (item.treasure === 1 ? '交付期限至：' : '保质期至：')
          + (d.getMonth() + 1) + '月' + d.getDate() + '日 ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
          + (this.freshRemain ? '（' + this.freshRemain + '）' : '');
      }
      if (item.itemDesc != null && item.itemDesc != '') {
        t += '\n' + item.itemDesc;
      }
      return t;
    },
    infoText() {
      const item = this.item;
      // 体力/增益类道具：卡片小字给「体力+N」（多增益只给个数，点开看明细）
      // （数值来自配置，描述里不写；变质的不显示，避免误导）
      const effectShort = effectCardText(item);
      if (effectShort) {
        return effectShort;
      }
      if (item.strength != null && item.strength > -1) {
        return '体力:' + item.strength;
      }
      if (item.durability != null && item.durability > -1) {
        return '评分:' + item.score + '\n耐久:' + item.durability;
      }
      if (item.score != null && item.score > -1) {
        return '评分:' + item.score;
      }
      return '';
    }
  },
  methods: {
    // 点击热区：图标 / 保鲜角标 / 名字 / 效果小字 都算「想看说明」——
    // 以前只绑在 <img> 上，玩家点右上角的「剩3小时」角标或道具名毫无反应（角标是 img 的兄弟节点）。
    // 现在统一由卡片根节点接管：
    //   ① showDescOnClick=false（地面拾取等）时直接放行，让事件继续冒泡给父级 @click；
    //   ② 落在卡片内嵌的操作链接（.opts 里的使用/装备/扔掉/删除）上时放行，避免点操作还弹说明；
    //   ③ 其余一律停止冒泡并弹出说明。
    onCardClick(e) {
      if (!this.showDescOnClick) return;
      if (this.inOpts(e.target)) return;
      e.stopPropagation();
      this.showDesc();
    },
    // 判断点击是否落在 slot 内嵌的操作区（.opts）
    inOpts(target) {
      let el = target;
      while (el && el !== this.$el) {
        if (el.classList && el.classList.contains('opts')) return true;
        el = el.parentElement;
      }
      return false;
    },
    // 读取当前玩家体力（用于把「可恢复 X」换算成「当前 → 使用后」）；拿不到时降级
    currentDetail() {
      try {
        const store = this.$store;
        if (store && store.state && store.state.user && store.state.user.fantasyUserDetail) {
          return store.state.user.fantasyUserDetail;
        }
      } catch (e) {
        // 无 Vuex 场景（独立预览/单测）忽略
      }
      return {};
    },
    // 移动端无 hover，点击图片显示道具效果说明与保鲜信息（桌面端 title 悬停保留）
    showDesc() {
      const item = this.item;
      if (!item) return;
      let msg = item.itemDesc || '';
      // 效果行放在描述最前面：数值全部由配置生成，描述只留风味文案
      const lines = this.effectLines;
      if (lines.length > 0) {
        msg = msg ? lines.join('\n') + '\n' + msg : lines.join('\n');
      }
      // 保鲜：与 title 口径一致——未变质展示到期时间，已变质展示处置提示
      if (item.state === 'spoiled') {
        const spoiledTip = '已变质：不可食用，可到果园的堆肥点堆成肥料（5 个变质物 → 1 个【肥料】）';
        msg = msg ? msg + '；' + spoiledTip : spoiledTip;
      } else if (item.expireAt && item.expireAt > 0) {
        const d = new Date(item.expireAt);
        const pad = n => (n < 10 ? '0' + n : n);
        const shelf = (item.treasure === 1 ? '交付期限至：' : '保质期至：')
          + (d.getMonth() + 1) + '月' + d.getDate() + '日 ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
          + (this.freshRemain ? '（' + this.freshRemain + '）' : '');
        msg = msg ? msg + '；' + shelf : shelf;
      }
      if (msg) {
        this.$notify({
          title: item.itemName,
          message: msg,
          duration: 4000,
          // 效果行多行展示（white-space:pre-line，样式在 App.vue 全局）
          customClass: 'effect-multiline'
        });
      }
    }
  }
};
</script>

<style scoped>
.fantasy-item-card {
  display: inline-block;
  text-align: center;
}
.img-wrapper {
  position: relative;
  display: inline-block;
}
.img-wrapper img {
  height: 40px;
  display: block;
}
.itemName {
  font-size: 11px;
  color: #333;
  line-height: 1.3;
  text-align: center;
  word-break: break-all;
  padding: 2px 0;
  overflow: hidden;
  max-height: 28px;
}
.itemInfo {
  font-size: 10px;
  color: #888;
  line-height: 1.2;
  text-align: center;
  white-space: pre-line;
}
.stackCount {
  position: absolute;
  bottom: 0;
  font-size: 0.75em;
  width: 100%;
  background-color: rgba(233, 141, 66, 0.85);
  color: #fff;
  font-weight: bold;
  text-shadow: 1px 1px 1px rgba(0,0,0,0.3);
}
/* ---- 保鲜（perish_v1）---- */
.img-spoiled {
  filter: grayscale(1);
  opacity: 0.75;
}
/* 变质：名称同步置灰，与新鲜道具一眼可辨 */
.card-spoiled .itemName {
  color: #999;
}
.freshness-badge {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0.72em;
  padding: 0 3px;
  border-radius: 0 0 0 4px;
  line-height: 1.4;
  color: #fff;
  font-weight: bold;
  text-shadow: 1px 1px 1px rgba(0,0,0,0.3);
}
.freshness-badge.expiring {
  background-color: #ff9800;
}
.freshness-badge.expiring.hot {
  background-color: #e53935;
}
.freshness-badge.spoiled {
  background-color: #8d8d8d;
}
</style>
